import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApiSuccess,
  PaginatedResponse,
  PaymentIncident,
  PaymentIncidentDetail,
  PaymentIncidentFilters,
  ManualRefundPaymentIncidentDto,
  UpdatePaymentIncidentDto,
} from '@alamo/types';

import { apiClient } from '../../client';
import { queryKeys } from '../../query-keys';

export function usePaymentIncidents(filters?: PaymentIncidentFilters) {
  return useQuery({
    queryKey: queryKeys.paymentIncidents.list(filters as Record<string, unknown> | undefined),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<{ count: number; rows: PaymentIncident[] }>>(
        '/admin/payment-incidents',
        { params: filters },
      );
      const raw = response.data.data;
      const limit = filters?.limit ?? 20;
      return {
        items: raw.rows,
        total: raw.count,
        page: filters?.page ?? 1,
        limit,
        totalPages: Math.ceil(raw.count / limit),
      } satisfies PaginatedResponse<PaymentIncident>;
    },
  });
}

export function usePaymentIncidentOpenCount(options?: {
  enabled?: boolean;
  refetchInterval?: number;
}) {
  return useQuery({
    queryKey: queryKeys.paymentIncidents.openCount(),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<{ count: number }>>(
        '/admin/payment-incidents/open-count',
      );
      return response.data.data;
    },
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function usePaymentIncident(id: string) {
  return useQuery({
    queryKey: queryKeys.paymentIncidents.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<PaymentIncidentDetail>>(
        `/admin/payment-incidents/${id}`,
      );
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useUpdatePaymentIncident(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpdatePaymentIncidentDto) => {
      const response = await apiClient.patch<ApiSuccess<PaymentIncidentDetail>>(
        `/admin/payment-incidents/${id}`,
        dto,
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.paymentIncidents.all });
    },
  });
}

function financialActionHeaders(): Record<string, string> {
  return { 'Idempotency-Key': crypto.randomUUID() };
}

export function useRequestPaymentIncidentRefund(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (retry: boolean) => {
      const response = await apiClient.post<ApiSuccess<PaymentIncidentDetail>>(
        `/admin/payment-incidents/${id}/${retry ? 'retry-refund' : 'refund'}`,
        undefined,
        { headers: financialActionHeaders() },
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.paymentIncidents.all });
    },
  });
}

export function useRecordManualPaymentIncidentRefund(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: ManualRefundPaymentIncidentDto) => {
      const response = await apiClient.post<ApiSuccess<PaymentIncidentDetail>>(
        `/admin/payment-incidents/${id}/manual-refund`,
        dto,
        { headers: financialActionHeaders() },
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.paymentIncidents.all });
    },
  });
}

export function useCreatePaymentIncidentCommissionAdjustment(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (note?: string) => {
      const response = await apiClient.post<ApiSuccess<PaymentIncidentDetail>>(
        `/admin/payment-incidents/${id}/commission-adjustment`,
        { note },
        { headers: financialActionHeaders() },
      );
      return response.data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.paymentIncidents.all });
    },
  });
}
